import { useRef, useState, type ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { Upload } from 'lucide-react'
import { tokens } from '../theme'

/**
 * Whether a file matches an `accept` list as a file input reads it: extensions
 * (`.csv`), exact types (`text/csv`) and wildcards (`image/*`). An empty list
 * accepts anything.
 */
export function fileAccepted(file: File, accept?: string): boolean {
  const rules = (accept ?? '').split(',').map((r) => r.trim().toLowerCase()).filter(Boolean)
  if (!rules.length) return true
  const name = file.name.toLowerCase()
  const type = (file.type || '').toLowerCase()
  return rules.some((r) =>
    r.startsWith('.') ? name.endsWith(r)
      : r.endsWith('/*') ? type.startsWith(r.slice(0, -1))
        : type === r)
}

export interface FileDropZoneProps {
  /** The chosen or dropped files that match `accept`; one at most unless `multiple`. */
  onFiles: (files: File[]) => void
  /** Dropped files that do not match `accept`, so the caller can say why nothing happened. */
  onRejected?: (files: File[]) => void
  /** As a file input's `accept`. Applied to dropped files too. */
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** The main line, e.g. "Drop a file here, or choose one". */
  label: ReactNode
  /** A second, quieter line: formats, size limits. */
  hint?: ReactNode
  /** Shown in place of the label while the caller works on a file. */
  busyLabel?: ReactNode
  busy?: boolean
  /** Less padding, no icon: for a dialog or a row of controls. */
  compact?: boolean
  /** The hidden input's accessible name; defaults to the label when it is text. */
  inputLabel?: string
}

/**
 * Where a file goes in: drop it on the box, or click (Enter, Space) to choose
 * one. Every upload in the interface uses it, so dropping works the same
 * everywhere and the dashed box always means "a file goes here".
 */
export function FileDropZone({
  onFiles, onRejected, accept, multiple, disabled, label, hint, busyLabel, busy, compact, inputLabel,
}: FileDropZoneProps) {
  const input = useRef<HTMLInputElement>(null)
  const depth = useRef(0)
  const [over, setOver] = useState(false)
  const off = disabled || busy

  const take = (list: FileList | null | undefined) => {
    const files = [...(list ?? [])]
    if (!files.length) return
    const ok = files.filter((f) => fileAccepted(f, accept))
    const bad = files.filter((f) => !fileAccepted(f, accept))
    if (bad.length) onRejected?.(bad)
    if (ok.length) onFiles(multiple ? ok : ok.slice(0, 1))
  }
  const pick = () => { if (!off) input.current?.click() }

  return (
    <Box
      role="button"
      tabIndex={off ? -1 : 0}
      aria-disabled={off}
      onClick={pick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick() }
      }}
      onDragEnter={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        depth.current += 1
        if (!off) setOver(true)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = off ? 'none' : 'copy'
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) setOver(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        depth.current = 0
        setOver(false)
        if (!off) take(e.dataTransfer.files)
      }}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: compact ? 1 : 2,
        p: compact ? 3 : 6, textAlign: 'center',
        cursor: off ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        border: `1px dashed ${over ? tokens.color.brandTeal : tokens.color.ink300}`,
        borderRadius: `${tokens.radius.md}px`,
        backgroundColor: over ? tokens.color.brandGreen100 : tokens.color.ink50,
        color: 'text.secondary',
        transition: `border-color ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}, background-color ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
        '&:hover': { borderColor: off ? tokens.color.ink300 : tokens.color.brandTeal },
        '&:focus-visible': { outline: 'none', boxShadow: tokens.shadow.ring },
      }}
    >
      <input
        ref={input}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        aria-label={inputLabel ?? (typeof label === 'string' ? label : undefined)}
        onChange={(e) => { take(e.target.files); e.target.value = '' }}
      />
      {!compact && <Upload size={20} strokeWidth={1.5} aria-hidden />}
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
        {busy && busyLabel ? busyLabel : label}
      </Typography>
      {hint && <Typography variant="caption" sx={{ maxWidth: 420 }}>{hint}</Typography>}
    </Box>
  )
}
